/**
 * Documents API
 * CRUD for documents and entity relations
 */

import { apiFetch, API_BASE_URL, getAuthHeader } from './config';

export interface CorpusDocument {
  document_id: string;
  name: string;
  document_type: string;
  abstract: string | null;
  version: string | null;
  status: string;
  location: string | null;
  domain: string | null;
  tags: string[] | null;
  object_type: string | null;
  entity_type: string | null;
  source_url: string | null;
  review_status: string | null;
  registered_by: string | null;
  series: string | null;
  sequence: number | null;
  content_sha256: string | null;
  s3_key: string | null;
  s3_version_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentCreateInput {
  name: string;
  document_type: string;
  abstract?: string;
  version?: string;
  status?: string;
  location?: string;
  domain?: string;
  tags?: string[];
  object_type?: string;
  related_standards?: string[];
}

export type DocumentUpdateInput = Partial<DocumentCreateInput>;

export interface ObjectRelation {
  relation_id: string;
  relation_type: string;
  direction: string;
  target_id: string;
  target_type: string;
  target: {
    id: string;
    name: string;
    object_type?: string;
    entity_type?: string;
    document_type?: string;
    category?: string;
    maturity?: string;
    status?: string;
    active?: boolean;
  };
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface DocumentsListResponse {
  Records: CorpusDocument[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface RelationsResponse {
  object_id: string;
  relations: ObjectRelation[];
  total: number;
}

export const documentsApi = {
  list: async (params?: {
    q?: string;
    document_type?: string;
    domain?: string;
    status?: string;
    object_type?: string;
    include_archived?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.q) searchParams.set('q', params.q);
    if (params?.document_type) searchParams.set('document_type', params.document_type);
    if (params?.domain) searchParams.set('domain', params.domain);
    if (params?.status) searchParams.set('status', params.status);
    if (params?.object_type) searchParams.set('object_type', params.object_type);
    if (params?.include_archived) searchParams.set('include_archived', 'true');
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return apiFetch<DocumentsListResponse>(`/api/documents${query ? `?${query}` : ''}`);
  },

  create: async (input: DocumentCreateInput) => {
    return apiFetch<{ Records: CorpusDocument[] }>('/api/documents', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  update: async (documentId: string, input: DocumentUpdateInput) => {
    return apiFetch<{ Records: CorpusDocument[] }>(`/api/documents/${encodeURIComponent(documentId)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  archive: async (documentId: string, reason?: string) => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    return apiFetch<{ Records: unknown[] }>(`/api/documents/${encodeURIComponent(documentId)}${params}`, {
      method: 'DELETE',
    });
  },

  getRelations: async (objectId: string, direction = 'both') => {
    return apiFetch<RelationsResponse>(`/api/relations/${encodeURIComponent(objectId)}?direction=${direction}`);
  },

  downloadExport: async (params?: { object_type?: string; include_archived?: boolean }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('limit', '10000');
    if (params?.object_type) searchParams.set('object_type', params.object_type);
    if (params?.include_archived) searchParams.set('include_archived', 'true');
    const query = searchParams.toString();
    const response = await fetch(`${API_BASE_URL}/api/documents?${query}`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    });
    const data = await response.json();
    const blob = new Blob([JSON.stringify(data.data || data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `documents-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  getContentUrl: async (documentId: string) => {
    return apiFetch<{ document_id: string; name: string; url: string; expires_in: number; content_sha256: string }>(
      `/api/documents/${encodeURIComponent(documentId)}/content`
    );
  },

  createRelation: async (input: {
    source_object_id: string;
    source_object_type: string;
    target_object_id: string;
    target_object_type: string;
    relation_type: string;
    metadata?: Record<string, unknown>;
  }) => {
    return apiFetch<{ Records: unknown[] }>('/api/relations', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};
